import { ComponentType } from "react";

import { eagerRoute, makeRoutes, MakeRoutesOpts } from "common/router/utils/routes";
import { makeBasePath, makeParentUrls } from "common/router/utils/urls";

import { INITIAL_CLIENT_DATA } from "client/common/utils";
import { AccountPaths, useAccountRoutes, makeAccountUrls } from "client/accounts/routes";
import { CheckoutPaths, useCheckoutRoutes, makeCheckoutUrls } from "client/checkout/routes";
import { LinkPaths, useLinkRoutes, makeLinkUrls } from "client/links/routes";
import { useOnboardingRoutes, makeOnboardingUrls, OnboardingPaths } from "client/onboarding/routes";
import { useOrderRoutes, makeOrderUrls, OrderPaths } from "client/orders/routes";
import {
  usePrescriptionRoutes,
  makePrescriptionUrls,
  PrescriptionPaths,
} from "client/prescriptions/routes";
import { useProductRoutes, makeProductUrls, ProductPaths } from "client/products/routes";
import {
  useProductCollectionRoutes,
  makeProductCollectionUrls,
  ProductCollectionPaths,
} from "client/productCollections/routes";
import { COLLECTION_PAGE_BY_KIND } from "client/productCollections/constants";
import { StorefrontCollectionKind } from "client/productCollections/types";
import { useSchedulerRoutes, makeSchedulerUrls, SchedulerPaths } from "client/scheduler/routes";
import { useCarePlanRoutes, makeCarePlanUrls, CarePlanPaths } from "client/carePlans/routes";
import {
  useSubscriptionRoutes,
  makeSubscriptionUrls,
  SubscriptionPaths,
} from "client/subscriptions/routes";
import { useSupportRoutes, makeSupportUrls, SupportPaths } from "client/support/routes";
import { HomePaths, useHomeRoutes, makeHomeUrls } from "client/home/routes";
import { InboxPaths, useInboxRoutes, makeInboxUrls } from "client/inbox/routes";

import { ClientModuleRoute } from "./types";

export type Paths = {
  accounts: AccountPaths;
  carePlans: CarePlanPaths;
  checkout: CheckoutPaths;
  links: LinkPaths;
  onboarding: OnboardingPaths;
  orders: OrderPaths;
  prescriptions: PrescriptionPaths;
  products: ProductPaths;
  productCollections: ProductCollectionPaths;
  subscriptions: SubscriptionPaths;
  support: SupportPaths;
  inbox: InboxPaths;
  scheduler: SchedulerPaths;
  home: HomePaths;
};

export const basePath = makeBasePath("/pet");

export const useClientRoutes = (
  opts: MakeRoutesOpts & {
    MainComponent: ComponentType;
    NotFoundComponent: ComponentType;
  },
) => {
  const isHdEnabled = INITIAL_CLIENT_DATA.clinic.primary_tag.is_hd_enabled;

  const allRoutes: ClientModuleRoute[] = [
    { path: "account", children: useAccountRoutes(opts), pageType: "accounts" },
    { path: "scheduler", children: useSchedulerRoutes(opts), pageType: "scheduler" },
    { path: "support", children: useSupportRoutes(opts), pageType: "support" },
    // HD-dependent routes
    { path: "care-plans", children: useCarePlanRoutes(opts), pageType: "care_plans" },
    { path: "checkout", children: useCheckoutRoutes(opts), requiresHd: true, pageType: "checkout" },
    { path: "links", children: useLinkRoutes(opts), requiresHd: true, pageType: "links" },
    {
      path: "onboarding",
      children: useOnboardingRoutes(opts),
      requiresHd: true,
      pageType: "onboarding",
    },
    { path: "orders", children: useOrderRoutes(opts), requiresHd: true, pageType: "orders" },
    {
      path: "prescriptions",
      children: usePrescriptionRoutes(opts),
      requiresHd: true,
      pageType: "prescriptions",
    },
    { path: "products", children: useProductRoutes(opts), requiresHd: true, pageType: "products" },
    // Public URL keeps the marketing word "brands"; the internal entity is a
    // StorefrontProductCollection.
    {
      path: COLLECTION_PAGE_BY_KIND[StorefrontCollectionKind.Brand].path,
      children: useProductCollectionRoutes(opts, StorefrontCollectionKind.Brand),
      requiresHd: true,
      pageType: "brands",
    },
    // Deals pages are StorefrontProductCollections too, with kind Rebate. Not to be confused
    // with /products/deals, the catalog-wide deals page.
    {
      path: COLLECTION_PAGE_BY_KIND[StorefrontCollectionKind.Rebate].path,
      children: useProductCollectionRoutes(opts, StorefrontCollectionKind.Rebate),
      requiresHd: true,
      pageType: "deals",
    },
    {
      path: "subscriptions",
      children: useSubscriptionRoutes(opts),
      requiresHd: true,
      pageType: "subscriptions",
    },
    { path: "inbox", children: useInboxRoutes(opts), pageType: "inbox" },
    // Catch-all route - always available, but shows 404 when HD is disabled
    { path: "*", children: useHomeRoutes(opts), pageType: "home" },
  ];

  // Filter routes based on HD enabled status
  const filteredRoutes = allRoutes.filter((route) => !route.requiresHd || isHdEnabled);

  return makeRoutes(opts, [
    eagerRoute({
      path: basePath,
      element: <opts.MainComponent />,
      children: makeRoutes(
        { ...opts, NotFoundComponent: null },
        filteredRoutes.map(({ pageType, ...route }) => ({
          ...route,
          meta: { pageType },
        })),
      ),
    }),
  ]);
};

export const urls = makeParentUrls(basePath, {
  accounts: makeAccountUrls(`${basePath}/account`),
  carePlans: makeCarePlanUrls(`${basePath}/care-plans`),
  checkout: makeCheckoutUrls(`${basePath}/checkout`),
  links: makeLinkUrls(`${basePath}/links`),
  onboarding: makeOnboardingUrls(`${basePath}/onboarding`),
  orders: makeOrderUrls(`${basePath}/orders`),
  prescriptions: makePrescriptionUrls(`${basePath}/prescriptions`),
  products: makeProductUrls(`${basePath}/products`),
  productCollections: makeProductCollectionUrls(`${basePath}/brands`),
  subscriptions: makeSubscriptionUrls(`${basePath}/subscriptions`),
  support: makeSupportUrls(`${basePath}/support`),
  inbox: makeInboxUrls(`${basePath}/inbox`),
  scheduler: makeSchedulerUrls(`${basePath}/scheduler`),
  home: makeHomeUrls(`${basePath}`),
});

/**
 * Any links to external sites.
 *
 * Unlike regular urls, these are just strings because they are not managed by
 * the clinic app and need to rely directly on native browser navigation.
 */
export const externalUrls = {
  disposalOfUnusedMedicine: () =>
    "https://www.fda.gov/consumers/consumer-updates/where-and-how-dispose-unused-medicines",
  helpCenter: () => "https://help.ourvet.com/en/",
  safeNeedleDisposal: () => "https://safeneedledisposal.org/",
  nabp: () => "https://safe.pharmacy/",
  /**
   * Signs a client out of their current session
   * Signout is treated as an external URL because it is not managed by the FE and requires
   * a hard navigation.
   */
  signout: () => `${basePath}/signout/`,
};
